/**
 * Animations (2026-10-12): « complètes », « réduites » (fades only: nothing moves, nothing loops) or
 * « désactivées », chosen per workstation, and by default the system's own choice (« reduce motion »).
 * The level is set on the page (`data-motion`), where the stylesheet reads it; the 3D scenes and the
 * numbers that roll read `motion`.
 */
import { ref } from 'vue'

export type MotionChoice = 'system' | 'full' | 'reduced' | 'off'
export type MotionLevel = 'full' | 'reduced' | 'off'

export const MOTION_CHOICES: readonly MotionChoice[] = ['system', 'full', 'reduced', 'off']
const KEY = 'tsh-motion'

const isChoice = (v: string | null): v is MotionChoice => MOTION_CHOICES.includes(v as MotionChoice)

export function readMotion(): MotionChoice {
  // Private browsing may refuse storage: the app must still render.
  try {
    const v = localStorage.getItem(KEY)
    return isChoice(v) ? v : 'system'
  } catch {
    return 'system'
  }
}

const reduceQuery = (): MediaQueryList | null => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)') } catch { return null }
}

/** What a choice gives here: the system's « reduce motion » reads as reduced. */
export function effectiveMotion(choice: MotionChoice, systemReduces = reduceQuery()?.matches ?? false): MotionLevel {
  if (choice !== 'system') return choice
  return systemReduces ? 'reduced' : 'full'
}

/** The workstation's choice, shared by every control that shows or changes it. */
export const motionChoice = ref<MotionChoice>(readMotion())
/** The level in force: what animations may do now. */
export const motion = ref<MotionLevel>(effectiveMotion(motionChoice.value))

function apply(): void {
  motion.value = effectiveMotion(motionChoice.value)
  document.documentElement.setAttribute('data-motion', motion.value)
}

export function setMotion(c: MotionChoice): void {
  motionChoice.value = c
  try { localStorage.setItem(KEY, c) } catch { /* kept for this visit */ }
  apply()
}

/** Set on the page once, before the first screen; follows the system's choice when it changes. */
export function startMotion(): void {
  apply()
  reduceQuery()?.addEventListener('change', apply)
}

/** Whether things may move (a slide, a roll, a 3D scene turning); a fade is allowed when reduced. */
export const moves = (): boolean => motion.value === 'full'
